<template>
  <mi-btn-dropdown
    :disable="isConfigurationFillUpInProgress"
    :menu-offset="[0, 4]"
    class="fill-up-menu-btn"
    label="Fill options"
    auto-close
    dense
    outline
    icon-right="caret-up"
  >
    <mi-list>
      <!-- Menu options -->
      <mi-list-item
        v-for="({ title }, fillUpType) in FILL_UP_TYPES"
        :key="fillUpType"
        clickable
        @click="handleFillUpOptionClick(fillUpType)"
      >
        <mi-list-item-section>
          <mi-list-item-label
            v-if="fillUpType ===FILL_UP_TYPES.removeAllSelectedChoices.key "
            class="remove-all-choices"
          >
            <span class="q-mr-xl">{{ title }}</span>
            <mi-icon name="trash" size="16px"></mi-icon>
          </mi-list-item-label>
          <mi-list-item-label v-else>
            {{ title }}
          </mi-list-item-label>
        </mi-list-item-section>
      </mi-list-item>
    </mi-list>
  </mi-btn-dropdown>
  <configuration-remove-all-choices-dialog
    :model-value="isRemoveAllChoicesDialogOpen"
    :update-selected-choices="updateSelectedChoices"
    @update:model-value="val => isRemoveAllChoicesDialogOpen = val"
  ></configuration-remove-all-choices-dialog>
</template>

<script>
  import { createNamespacedHelpers } from 'vuex'

  import notify from '@/utils/notify'
  import recordAnalytics from '@/utils/analytics/recordAnalytics'
  import {
    CLEAR_SELECTED_CHOICES,
    SET_SELECTED_CHOICES,
    SET_CURRENT_CONFIGURATION
  } from '@/store/modules/pac/mutationTypes'
  import { PAC_CONFIG_FILLED_UP } from '@/utils/analytics/constants'
  import { poll } from '@/api/polling'
  import {
    fillConfigurationAllValidChoices,
    fillConfigurationComplete,
    fillConfigurationSingleValidChoices,
    fillConfigurationValidChoicesData,
    fillConfigurationValidCompleteData
  } from '@/api'
  import ConfigurationRemoveAllChoicesDialog from './ConfigurationRemoveAllChoicesDialog.vue'
  import MiIcon from '../../../packages/@mi-library/MiIcon/MiIcon.vue'

  const { mapActions, mapMutations, mapState } = createNamespacedHelpers('pac')

  export default {
    name: 'ConfigurationFillUpMenu',
    components: { MiIcon, ConfigurationRemoveAllChoicesDialog },
    data: () => ({
      FILL_UP_TYPES: {
        complete: {
          title: 'With valid choices',
          fillUpFnAsync: fillConfigurationComplete,
          fillUpFnAsyncData: fillConfigurationValidCompleteData
        },
        singleValidChoices: {
          title: 'With only one valid choice',
          fillUpFnAsync: fillConfigurationSingleValidChoices,
          fillUpFnAsyncData: fillConfigurationValidChoicesData
        },
        allValidChoices: {
          title: 'With every valid choices',
          fillUpFnAsync: fillConfigurationAllValidChoices,
          fillUpFnAsyncData: fillConfigurationValidChoicesData
        },
        removeAllSelectedChoices: {
          key: 'removeAllSelectedChoices',
          title: 'Remove all choices'
        }
      },
      isConfigurationFillUpInProgress: false,
      clearPoll: null,
      isRemoveAllChoicesDialogOpen: false
    }),
    computed: {
      ...mapState({
        currentConfiguration: 'currentConfiguration',
        lockedChoicesIds: 'lockedChoicesIds',
        validationResultsLength: ({ validation = {} } = {}) => validation.results?.length || 0
      })
    },
    beforeUnmount() {
      this.clearPoll?.()
    },
    methods: {
      ...mapActions(['validateCurrentConfigurationV2']),
      ...mapMutations({
        CLEAR_SELECTED_CHOICES,
        SET_SELECTED_CHOICES,
        SET_CURRENT_CONFIGURATION
      }),

      async handleFillUpOptionClick(fillUpType = '') {
        if (fillUpType === this.FILL_UP_TYPES.removeAllSelectedChoices.key) {
          this.isRemoveAllChoicesDialogOpen = true
          return
        }

        const notificationFn = this.$q.notify({
          message: '(1/2) Validating configuration',
          caption: 'Please wait...',
          type: 'info',
          timeout: 0,
          spinner: true,
          group: false
        })

        this.isConfigurationFillUpInProgress = true

        try {
          await this.getValidateConfiguration()

          if (this.validationResultsLength) {
            notify({
              title: 'Warning',
              content: 'The configuration is invalid and it\'s not possible to fill it up',
              type: 'warning'
            })
          }
          else {
            notificationFn({ message: '(2/2) Filling up configuration' })

            const params = {
              selectedChoices: this.currentConfiguration.selectedChoices,
              planningPeriodRange: this.currentConfiguration.planningPeriodRange,
              encodedBusinessName: this.currentConfiguration.productModel?.encodedBusinessName,
              ruleIdsToIgnore: this.currentConfiguration.ignoreList?.map(rule => rule.id)
            }

            // eslint-disable-next-line security/detect-object-injection
            const fillUpTypeObj = this.FILL_UP_TYPES[fillUpType]

            const choices = await this.fillUpAsync(fillUpTypeObj, params)

            this.updateSelectedChoices(choices)

            // Analytics
            recordAnalytics(
              PAC_CONFIG_FILLED_UP,
              // eslint-disable-next-line security/detect-object-injection
              { type: this.FILL_UP_TYPES[fillUpType].title }
            )
          }
        }
        finally {
          this.isConfigurationFillUpInProgress = false

          notificationFn()
        }
      },
      async fillUpAsync(fillUpTypeObj, params) {
        const { executePoll, clearPoll } = poll()
        this.clearPoll = clearPoll

        const { choices } = await executePoll({
          correlationIdEndpoint: {
            getCorrelationId: fillUpTypeObj?.fillUpFnAsync,
            params
          },
          asyncEndpoint: fillUpTypeObj?.fillUpFnAsyncData
        })

        return choices
      },
      async getValidateConfiguration() {
        const { executePoll, clearPoll } = poll()
        this.clearPoll = clearPoll
        return this.validateCurrentConfigurationV2(executePoll)
      },
      updateSelectedChoices(choices = []) {
        let updatedOptionsMissingChoices = []

        if (this.currentConfiguration?.validation && choices.length > 0) {
          const optionsSelectedChoices = choices.map(choice => choice.optionId)
          updatedOptionsMissingChoices = this.currentConfiguration.validation.optionsMissingPreviousSelectedChoices
            .filter(id => !optionsSelectedChoices.includes(id))
        }

        this.CLEAR_SELECTED_CHOICES()
        this.SET_SELECTED_CHOICES(choices)
        this.SET_CURRENT_CONFIGURATION({
          ...this.currentConfiguration,
          selectedChoices: choices.map(choice => ({
            choice,
            locked: !!this.lockedChoicesIds.get(choice.optionId)?.includes(choice.id)
          })),
          validation: { optionsMissingPreviousSelectedChoices: updatedOptionsMissingChoices }
        })
      }
    }
  }
</script>

<style lang="scss" scoped>
  .fill-up-menu-btn.mi-btn.q-btn--dense {
    height: 32px;

    ::v-deep(.q-btn__content) {
      line-height: 22px;
    }
  }

  .q-item__label {
    // stylelint-disable declaration-no-important
    font-size: 18px !important;
  }

  .remove-all-choices {
    color: $mi-red-500;
  }

  .q-menu .q-item:nth-child(1):before,
  .q-menu .q-item:nth-child(2):before,
  .q-menu .q-item:nth-child(3):before {
    position: unset;
  }
</style>
